import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { SUPPORTED_APP_NAMES } from '~/features/fleets/switcher/configs/fleetSwitcher.config';
import { getAppNameFromPath } from '~/features/navigationDrawer/config/navDrawer.config';
import { selectActingPermissionsContextIds } from '~/store/profile/profileSlice.selectors';

/**
 * Hook that determines if the fleet switcher section should be visible based on the
 * current route and the acting user's profile context.
 *
 * The fleet switcher is only shown on specific app routes that support multi-fleet
 * context (e.g. fleet management, organizations) and when the user has at least one
 * of organizationsId, hubsId, or fleetsId in their acting permissions context. This
 * matches the visibility logic in FleetSwitcherContainer, so callers use a single
 * flag to show/hide the switcher block and related UI (e.g. the divider below it).
 *
 * Note: getAppNameFromPath always returns a valid AppNames value with fallback logic,
 * so no additional null/undefined checking is needed.
 *
 * @returns Object containing:
 * - `isFleetSwitcherVisible`: True when the current route is a supported app and the
 *   user has profile context (org/hub/fleet). Use to decide whether to render the
 *   fleet switcher block and its divider.
 *
 * @example
 * ```tsx
 * const { isFleetSwitcherVisible } = useFleetSwitcherVisibility();
 *
 * return (
 *   <>
 *     {isFleetSwitcherVisible && (
 *       <>
 *         <FleetSwitcherContainer />
 *         <Divider />
 *       </>
 *     )}
 *     <MainContent />
 *   </>
 * );
 * ```
 */
const useFleetSwitcherVisibility = (): {
  isFleetSwitcherVisible: boolean;
} => {
  const { pathname } = useLocation();
  const actingPermissionsContextIds = useSelector(
    selectActingPermissionsContextIds
  );

  const isRouteSupported = useMemo(() => {
    const appName = getAppNameFromPath(pathname);
    return SUPPORTED_APP_NAMES.includes(appName);
  }, [pathname]);

  const hasProfileContexts = useMemo(
    () =>
      Boolean(
        actingPermissionsContextIds?.organizationsId ||
          actingPermissionsContextIds?.hubsId ||
          actingPermissionsContextIds?.fleetsId
      ),
    [
      actingPermissionsContextIds?.organizationsId,
      actingPermissionsContextIds?.hubsId,
      actingPermissionsContextIds?.fleetsId,
    ]
  );

  const isFleetSwitcherVisible = useMemo(
    () => isRouteSupported && hasProfileContexts,
    [isRouteSupported, hasProfileContexts]
  );

  return { isFleetSwitcherVisible };
};

export default useFleetSwitcherVisibility;
